import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash, ArrowUp, ArrowDown, FloppyDisk, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";

// ───────────────────────────────────────────────────────────────────────────
// REQUIREMENT FIELD TYPES (matches backend models.RequirementType)
// ───────────────────────────────────────────────────────────────────────────
const REQ_TYPES = [
  { v: "short_text", label: "Short text" },
  { v: "long_text", label: "Long text" },
  { v: "single_select", label: "Single select" },
  { v: "multi_select", label: "Multiple select" },
  { v: "yes_no", label: "Yes / No" },
  { v: "file", label: "File upload" },
  { v: "url", label: "URL" },
  { v: "email", label: "Email" },
  { v: "phone", label: "Phone" },
];

const _uid = () => "req_" + Math.random().toString(36).slice(2, 11);

// ───────────────────────────────────────────────────────────────────────────
// FAQ BUILDER (shared across create + edit)
// ───────────────────────────────────────────────────────────────────────────
function FaqBuilder({ items, onChange }) {
  const update = (i, patch) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const add = () => onChange([...items, { id: _uid(), q: "", a: "" }]);
  return (
    <div className="space-y-3" data-testid="faqs-builder">
      {items.length === 0 && (
        <div className="text-xs text-gray-500 rounded-lg border border-dashed border-gray-300 p-4">
          No FAQs yet. Add common buyer questions to reduce pre-sale messages.
        </div>
      )}
      {items.map((it, i) => (
        <div
          key={it.id}
          className="rounded-xl border border-gray-200 p-3 space-y-2"
          data-testid={`faq-row-${i}`}
        >
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">#{i + 1}</span>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => move(i, -1)} disabled={i === 0}
              data-testid={`faq-up-${i}`} className="h-7 px-1.5 ml-auto"
            >
              <ArrowUp size={14} />
            </Button>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => move(i, +1)} disabled={i === items.length - 1}
              data-testid={`faq-down-${i}`} className="h-7 px-1.5"
            >
              <ArrowDown size={14} />
            </Button>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => remove(i)}
              data-testid={`faq-remove-${i}`}
              className="h-7 px-1.5 text-red-500 hover:text-red-700"
            >
              <Trash size={14} />
            </Button>
          </div>
          <Input
            placeholder="Question" value={it.q}
            onChange={(e) => update(i, { q: e.target.value })}
            data-testid={`faq-q-${i}`}
          />
          <Textarea
            placeholder="Answer" rows={3} value={it.a}
            onChange={(e) => update(i, { a: e.target.value })}
            data-testid={`faq-a-${i}`}
          />
        </div>
      ))}
      <Button
        type="button" variant="outline" onClick={add}
        data-testid="faqs-add-btn" className="w-full"
      >
        <Plus size={14} className="mr-1.5" /> Add FAQ
      </Button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// REQUIREMENTS BUILDER (shared across create + edit)
// ───────────────────────────────────────────────────────────────────────────
function RequirementsBuilder({ fields, onChange }) {
  const update = (i, patch) => {
    const next = fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f));
    onChange(next);
  };
  const remove = (i) => onChange(
    fields.filter((_, idx) => idx !== i).map((f, k) => ({ ...f, order: k }))
  );
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= fields.length) return;
    const copy = [...fields];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy.map((f, k) => ({ ...f, order: k })));
  };
  const add = () => onChange([
    ...fields,
    {
      id: _uid(), type: "short_text", prompt: "", helper: "", required: true,
      options: [], file_types: [], max_files: 1, order: fields.length,
    },
  ]);
  return (
    <div className="space-y-3" data-testid="reqs-builder">
      {fields.length === 0 && (
        <div className="text-xs text-gray-500 rounded-lg border border-dashed border-gray-300 p-4">
          No buyer requirements yet. The buyer will see a single generic notes box at checkout.
          Add structured questions so they submit exactly what you need.
        </div>
      )}
      {fields.map((f, i) => (
        <div
          key={f.id}
          className="rounded-xl border border-gray-200 p-3 space-y-2"
          data-testid={`req-row-${i}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
              #{i + 1}
            </span>
            <Select value={f.type} onValueChange={(v) => update(i, { type: v })}>
              <SelectTrigger className="w-44 h-8 text-sm" data-testid={`req-type-${i}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REQ_TYPES.map((t) => (
                  <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <Switch
                checked={!!f.required}
                onCheckedChange={(v) => update(i, { required: v })}
                data-testid={`req-required-${i}`}
              />
              Required
            </label>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => move(i, -1)} disabled={i === 0}
              data-testid={`req-up-${i}`} className="h-7 px-1.5"
            >
              <ArrowUp size={14} />
            </Button>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => move(i, +1)} disabled={i === fields.length - 1}
              data-testid={`req-down-${i}`} className="h-7 px-1.5"
            >
              <ArrowDown size={14} />
            </Button>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => remove(i)}
              data-testid={`req-remove-${i}`}
              className="h-7 px-1.5 text-red-500 hover:text-red-700"
            >
              <Trash size={14} />
            </Button>
          </div>
          <Input
            placeholder="Question or prompt" value={f.prompt}
            onChange={(e) => update(i, { prompt: e.target.value })}
            data-testid={`req-prompt-${i}`}
          />
          <Input
            placeholder="Helper text (optional)" value={f.helper || ""}
            onChange={(e) => update(i, { helper: e.target.value })}
            className="text-sm"
          />
          {(f.type === "single_select" || f.type === "multi_select") && (
            <Input
              placeholder="Options (comma separated)"
              value={(f.options || []).join(", ")}
              onChange={(e) => update(i, {
                options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
              })}
              data-testid={`req-options-${i}`}
            />
          )}
          {f.type === "file" && (
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Allowed file types (pdf, png, …)"
                value={(f.file_types || []).join(", ")}
                onChange={(e) => update(i, {
                  file_types: e.target.value.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
                })}
              />
              <Input
                type="number" min={1} max={20} placeholder="Max files"
                value={f.max_files || 1}
                onChange={(e) => update(i, {
                  max_files: Math.max(1, Number(e.target.value) || 1),
                })}
              />
            </div>
          )}
        </div>
      ))}
      <Button
        type="button" variant="outline" onClick={add}
        data-testid="reqs-add-btn" className="w-full"
      >
        <Plus size={14} className="mr-1.5" /> Add requirement
      </Button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// FORM SHAPE  ← single source of truth for both create + edit
// ───────────────────────────────────────────────────────────────────────────
const _DEFAULT_FORM = {
  title: "", category_slug: "", description: "", seo_risk: "", tags: "",
  package_mode: "single",
  single: {
    name: "", title: "", price: "", days: "", revisions: 1,
    description: "", features: "",
  },
  basic:    { title: "Basic",    price: 99,  days: 5,  revisions: 1, features: "Core deliverable" },
  standard: { title: "Standard", price: 249, days: 8,  revisions: 2, features: "Extended scope\nStrategy call" },
  premium:  { title: "Premium",  price: 599, days: 12, revisions: 5, features: "White-glove onboarding\nPriority delivery\n90-day support" },
  included: "",
  requirement_fields: [],
  faqs: [],
};

const initialForm = () => JSON.parse(JSON.stringify(_DEFAULT_FORM));

// Hydrate the form from an existing Service document (edit mode).
function _formFromService(svc) {
  if (!svc) return initialForm();
  const f = initialForm();
  f.title = svc.title || "";
  f.category_slug = svc.category_slug || "";
  f.description = svc.description || "";
  f.seo_risk = svc.seo_risk || "";
  f.tags = (svc.tags || []).join(", ");
  f.package_mode = svc.package_mode || (svc.packages?.length === 1 ? "single" : "tiers");
  f.included = (svc.included || []).join("\n");
  if (f.package_mode === "single" && svc.packages?.[0]) {
    const p = svc.packages[0];
    f.single = {
      name: p.name || "",
      title: p.title || "",
      price: p.price ?? "",
      days: p.delivery_days ?? "",
      revisions: p.revisions ?? 1,
      description: p.description || "",
      features: (p.features || []).join("\n"),
    };
  }
  if (f.package_mode === "tiers") {
    for (const tier of ["basic", "standard", "premium"]) {
      const p = svc.packages?.find((x) => x.name === tier);
      if (p) {
        f[tier] = {
          title: p.title || tier,
          price: p.price ?? 0,
          days: p.delivery_days ?? 1,
          revisions: p.revisions ?? 0,
          features: (p.features || []).join("\n"),
        };
      }
    }
  }
  // Requirement fields — preserve every key the seller previously set.
  f.requirement_fields = (svc.requirement_fields || []).map((r, i) => ({
    id: r.id || _uid(),
    type: r.type || "short_text",
    prompt: r.prompt || "",
    helper: r.helper || "",
    required: r.required ?? true,
    options: r.options || [],
    file_types: r.file_types || [],
    max_files: r.max_files || 1,
    order: r.order ?? i,
  }));
  f.faqs = (svc.faqs || []).map((q) => ({
    id: _uid(), q: q.q || "", a: q.a || "",
  }));
  // For edit mode we also keep the existing images so the seller doesn't
  // have to re-upload them.
  return f;
}

// ───────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ───────────────────────────────────────────────────────────────────────────
/**
 * ServiceForm — shared between Create and Edit.
 *
 * Props:
 *   - mode: "create" | "edit"
 *   - meId: current user's id (used for namespacing localStorage drafts)
 *   - service?: existing service (edit only)
 *
 * Drafts are namespaced per (user, mode, serviceId) so editing one service
 * never clobbers the create draft, nor any other service's edit draft.
 */
export default function ServiceForm({ mode = "create", meId, service = null }) {
  const nav = useNavigate();
  const [cats, setCats] = useState([]);

  // Existing cover images on the service (edit mode only). Stored as URL
  // strings — sellers can remove them (NOT auto-deleted) and add new ones.
  const [existingImages, setExistingImages] = useState(service?.images || []);
  const [newImages, setNewImages] = useState([]);

  // Track package-mode switch confirmation
  const [pendingMode, setPendingMode] = useState(null);

  const _draftKey = useMemo(() => {
    if (!meId) return null;
    if (mode === "edit") return `mv_service_draft_v2_${meId}_edit_${service?.id}`;
    return `mv_service_draft_v2_${meId}_create`;
  }, [meId, mode, service]);

  const [form, setForm] = useState(() => (
    mode === "edit" ? _formFromService(service) : initialForm()
  ));
  const [draftLoaded, setDraftLoaded] = useState(false);

  // Baselines — snapshot of "last saved state" used to detect unsaved
  // changes. Kept in state (not refs) so React re-evaluates `dirty` when
  // they update after a save.
  const [baselineSig, setBaselineSig] = useState(() => JSON.stringify(
    mode === "edit" ? _formFromService(service) : initialForm()
  ));
  const [baselineImagesSig, setBaselineImagesSig] = useState(
    () => JSON.stringify(service?.images || [])
  );
  const dirty = useMemo(() => {
    if (!draftLoaded) return false;
    if (JSON.stringify(form) !== baselineSig) return true;
    if (JSON.stringify(existingImages) !== baselineImagesSig) return true;
    if (newImages.length > 0) return true;
    return false;
  }, [form, existingImages, newImages, draftLoaded, baselineSig, baselineImagesSig]);

  // Native beforeunload guard — covers refresh/close/external navigation.
  // In-app link clicks aren't intercepted by this; the localStorage draft
  // means a return-trip restores anyway, so we keep the UX simple.
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Load categories
  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);

  // Draft load (once per mount, gated by _draftKey availability).
  useEffect(() => {
    if (!_draftKey || draftLoaded) return;
    let restored = null;
    try {
      const raw = localStorage.getItem(_draftKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") restored = saved;
      }
    } catch {
      // ignore — drafts are best-effort
    }
    if (restored) {
      setForm({ ...initialForm(), ...restored });
      toast.success("Restored your unsaved draft");
    }
    setDraftLoaded(true);
  }, [_draftKey, draftLoaded]);

  // Draft autosave
  useEffect(() => {
    if (!_draftKey || !draftLoaded) return;
    try {
      localStorage.setItem(_draftKey, JSON.stringify(form));
    } catch {
      // quota or private-mode — skip silently
    }
  }, [form, _draftKey, draftLoaded]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const _hasDataInInactiveMode = (currentMode, nextMode) => {
    if (currentMode === "single" && nextMode === "tiers") {
      const s = form.single;
      return !!(s.title?.trim() || s.price || s.days || s.description?.trim() ||
                s.features?.trim());
    }
    if (currentMode === "tiers" && nextMode === "single") {
      for (const t of ["basic", "standard", "premium"]) {
        const x = form[t];
        if (x.title !== t.charAt(0).toUpperCase() + t.slice(1)) return true;
        if (x.features && x.features.trim() !== "Core deliverable" &&
            !["Extended scope\nStrategy call",
              "White-glove onboarding\nPriority delivery\n90-day support",
             ].includes(x.features.trim())) {
          return true;
        }
        if (x.price && Number(x.price) !== _DEFAULT_FORM[t].price) return true;
        if (x.days && Number(x.days) !== _DEFAULT_FORM[t].days) return true;
      }
    }
    return false;
  };

  const requestModeChange = (next) => {
    if (next === form.package_mode) return;
    if (_hasDataInInactiveMode(form.package_mode, next)) {
      setPendingMode(next);
      return;
    }
    update("package_mode", next);
  };

  const confirmModeChange = () => {
    if (pendingMode) update("package_mode", pendingMode);
    setPendingMode(null);
  };

  // Build packages array from the active mode's form data.
  const buildPackages = () => {
    if (form.package_mode === "single") {
      const s = form.single;
      const slug = (s.name || s.title || "package").trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "package";
      return [{
        name: slug,
        title: s.title?.trim() || s.name?.trim() || "Package",
        price: Number(s.price),
        delivery_days: Number(s.days),
        revisions: Number(s.revisions) || 0,
        features: (s.features || "").split("\n").map((x) => x.trim()).filter(Boolean),
        description: s.description?.trim() || null,
      }];
    }
    return ["basic", "standard", "premium"].map((tier) => ({
      name: tier,
      title: form[tier].title || tier.charAt(0).toUpperCase() + tier.slice(1),
      price: Number(form[tier].price),
      delivery_days: Number(form[tier].days),
      revisions: Number(form[tier].revisions) || 0,
      features: (form[tier].features || "").split("\n").map((x) => x.trim()).filter(Boolean),
    }));
  };

  const _validate = () => {
    if (form.package_mode === "single") {
      const s = form.single;
      if (!s.title?.trim()) return "Package name is required.";
      if (!(Number(s.price) > 0)) return "Price must be greater than $0.";
      if (!(Number(s.days) > 0)) return "Delivery time must be at least 1 day.";
    } else {
      for (const t of ["basic", "standard", "premium"]) {
        const x = form[t];
        if (!x.title?.trim()) return `${t} tier title is required.`;
        if (!(Number(x.price) > 0)) return `${t} tier price must be greater than $0.`;
        if (!(Number(x.days) > 0)) return `${t} tier delivery must be at least 1 day.`;
      }
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = _validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      const newImageUrls = newImages.map(
        (f) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.file_id}`
      );
      // Preserve existing images that the seller did not remove.
      const finalImages = [
        ...existingImages,
        ...newImageUrls,
      ];
      const fallback = ["https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900"];
      const payload = {
        title: form.title,
        category_slug: form.category_slug,
        description: form.description,
        images: finalImages.length ? finalImages : fallback,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        package_mode: form.package_mode,
        packages: buildPackages(),
        addons: [],
        faqs: (form.faqs || [])
          .filter((f) => (f.q || "").trim() && (f.a || "").trim())
          .map((f) => ({ q: f.q.trim(), a: f.a.trim() })),
        included: form.included.split("\n").map((s) => s.trim()).filter(Boolean),
        not_included: [],
        required_info: "",
        expected_outcome: "",
        refund_policy: "",
        seo_risk: form.seo_risk || null,
        requirement_fields: (form.requirement_fields || []).map((f, i) => ({
          ...f,
          order: i,
          options: f.options || [],
          file_types: f.file_types || [],
          max_files: f.max_files || 1,
        })),
      };
      let result;
      if (mode === "create") {
        const { data } = await api.post("/services", payload);
        result = data;
      } else {
        const { data } = await api.patch(`/services/${service.id}`, payload);
        result = data;
      }
      // Clear draft only on save success — refreshes/deploys preserve it.
      try { if (_draftKey) localStorage.removeItem(_draftKey); } catch { /* ignore */ }
      // Reset dirty marker so the navigation guard doesn't fire.
      setBaselineSig(JSON.stringify(form));
      setBaselineImagesSig(JSON.stringify(existingImages));
      setNewImages([]);
      toast.success(mode === "create" ? "Service published" : "Service updated");
      nav(`/service/${result.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const clearDraft = () => {
    if (!window.confirm(
      mode === "create"
        ? "Discard your unsaved service draft?"
        : "Discard your unsaved edits and reset to the saved version?"
    )) return;
    try { if (_draftKey) localStorage.removeItem(_draftKey); } catch { /* ignore */ }
    if (mode === "edit") {
      const fresh = _formFromService(service);
      setForm(fresh);
      setExistingImages(service?.images || []);
      setNewImages([]);
      setBaselineSig(JSON.stringify(fresh));
      setBaselineImagesSig(JSON.stringify(service?.images || []));
    } else {
      const fresh = initialForm();
      setForm(fresh);
      setExistingImages([]);
      setNewImages([]);
      setBaselineSig(JSON.stringify(fresh));
      setBaselineImagesSig(JSON.stringify([]));
    }
    toast.success("Draft cleared");
  };

  const removeExistingImage = (url) => {
    setExistingImages((arr) => arr.filter((u) => u !== url));
  };

  return (
    <>
      <form
        onSubmit={submit}
        className="space-y-5 max-w-2xl"
        data-testid={mode === "edit" ? "edit-service-form" : "new-service-form"}
      >
        <div className="flex items-center justify-end">
          <Button
            type="button" variant="ghost" size="sm" onClick={clearDraft}
            data-testid="clear-draft-btn" className="text-xs text-gray-500"
          >
            <FloppyDisk size={14} weight="duotone" className="mr-1" />
            {mode === "edit" ? "Reset to saved" : "Clear draft"}
          </Button>
        </div>

        <div>
          <Label>Title</Label>
          <Input
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            required data-testid="ns-title"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Category</Label>
            <Select
              value={form.category_slug}
              onValueChange={(v) => update("category_slug", v)}
            >
              <SelectTrigger data-testid="ns-category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {cats.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>SEO risk (optional)</Label>
            <Select
              value={form.seo_risk || "none"}
              onValueChange={(v) => update("seo_risk", v === "none" ? "" : v)}
            >
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="low">Low risk</SelectItem>
                <SelectItem value="medium">Medium risk</SelectItem>
                <SelectItem value="high">High risk</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <Textarea
            rows={5} value={form.description}
            onChange={(e) => update("description", e.target.value)}
            required data-testid="ns-description"
          />
        </div>

        <div>
          <Label>Tags (comma separated)</Label>
          <Input
            value={form.tags}
            onChange={(e) => update("tags", e.target.value)}
            placeholder="seo, audit, technical"
          />
        </div>

        <div>
          <Label>Cover images</Label>
          {existingImages.length > 0 && (
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2"
                 data-testid="existing-images">
              {existingImages.map((url) => (
                <div key={url}
                     className="relative rounded-lg overflow-hidden border border-gray-200 aspect-square group">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeExistingImage(url)}
                    aria-label="Remove image"
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/95 text-rose-600 shadow border border-gray-200 flex items-center justify-center opacity-90 hover:opacity-100"
                    data-testid={`remove-existing-image-${url}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <FileUploader
            folder="services" value={newImages} onChange={setNewImages}
            accept="image/*" max={6} imageMode
            label={existingImages.length ? "Upload more images" : "Upload images"}
            testId="ns-images"
          />
          {mode === "edit" && (
            <p className="text-[11px] text-gray-400 mt-1.5">
              Existing images are kept until you remove them.
            </p>
          )}
        </div>

        {/* PACKAGE MODE PICKER */}
        <div className="rounded-2xl border-2 border-gray-200 p-4">
          <Label className="block mb-2">Pricing structure</Label>
          <div className="grid grid-cols-2 gap-2 mb-4" role="radiogroup">
            <button
              type="button"
              data-testid="pkg-mode-single"
              onClick={() => requestModeChange("single")}
              className={`rounded-xl border-2 p-3 text-left text-sm ${
                form.package_mode === "single"
                  ? "border-mv-primary bg-mv-primary/5"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <div className="font-bold text-mv-secondary">Single package</div>
              <div className="text-xs text-gray-500 mt-0.5">
                One clear fixed offering. Best for productized services.
              </div>
            </button>
            <button
              type="button"
              data-testid="pkg-mode-tiers"
              onClick={() => requestModeChange("tiers")}
              className={`rounded-xl border-2 p-3 text-left text-sm ${
                form.package_mode === "tiers"
                  ? "border-mv-primary bg-mv-primary/5"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <div className="font-bold text-mv-secondary">Three packages</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Basic · Standard · Premium tiers. Best when buyers expect choice.
              </div>
            </button>
          </div>

          {form.package_mode === "single" ? (
            <div className="rounded-xl bg-gray-50 p-4 space-y-3"
                 data-testid="single-package-panel">
              <div>
                <Label>Package name</Label>
                <Input
                  value={form.single.title}
                  onChange={(e) => update("single", { ...form.single, title: e.target.value })}
                  placeholder="e.g. Complete SEO Website Build" required
                  data-testid="ns-single-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Price ($)</Label>
                  <Input
                    type="number" min={1} value={form.single.price}
                    onChange={(e) => update("single", { ...form.single, price: e.target.value })}
                    required data-testid="ns-single-price"
                  />
                </div>
                <div>
                  <Label>Delivery (days)</Label>
                  <Input
                    type="number" min={1} value={form.single.days}
                    onChange={(e) => update("single", { ...form.single, days: e.target.value })}
                    required data-testid="ns-single-days"
                  />
                </div>
              </div>
              <div>
                <Label>Revisions included</Label>
                <Input
                  type="number" min={0} value={form.single.revisions}
                  onChange={(e) => update("single", { ...form.single, revisions: e.target.value })}
                  data-testid="ns-single-revisions"
                />
              </div>
              <div>
                <Label>Package description (optional)</Label>
                <Textarea
                  rows={3} value={form.single.description}
                  onChange={(e) => update("single", { ...form.single, description: e.target.value })}
                  data-testid="ns-single-description"
                />
              </div>
              <div>
                <Label>What is included (one per line)</Label>
                <Textarea
                  rows={3} value={form.single.features}
                  onChange={(e) => update("single", { ...form.single, features: e.target.value })}
                  placeholder={"Kickoff call\nFinal deliverables"}
                  data-testid="ns-single-features"
                />
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3" data-testid="tiers-panel">
              {["basic", "standard", "premium"].map((tier) => (
                <div key={tier} className="rounded-xl border border-gray-200 p-3 space-y-2">
                  <div className="text-xs uppercase font-bold tracking-wider text-mv-primary">{tier}</div>
                  <Label>Title</Label>
                  <Input
                    value={form[tier].title}
                    onChange={(e) => update(tier, { ...form[tier], title: e.target.value })}
                    data-testid={`ns-${tier}-title`}
                  />
                  <Label className="mt-2">Price ($)</Label>
                  <Input
                    type="number" value={form[tier].price}
                    onChange={(e) => update(tier, { ...form[tier], price: e.target.value })}
                    required data-testid={`ns-${tier}-price`}
                  />
                  <Label className="mt-2">Delivery (days)</Label>
                  <Input
                    type="number" value={form[tier].days}
                    onChange={(e) => update(tier, { ...form[tier], days: e.target.value })}
                    required data-testid={`ns-${tier}-days`}
                  />
                  <Label className="mt-2">Revisions</Label>
                  <Input
                    type="number" value={form[tier].revisions}
                    onChange={(e) => update(tier, { ...form[tier], revisions: e.target.value })}
                  />
                  <Label className="mt-2">Features (one per line)</Label>
                  <Textarea
                    rows={3} value={form[tier].features}
                    onChange={(e) => update(tier, { ...form[tier], features: e.target.value })}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SHARED INCLUDED LIST (only meaningful in tiers mode) */}
        {form.package_mode === "tiers" && (
          <div>
            <Label>What is included across all tiers (one per line)</Label>
            <Textarea
              rows={3} value={form.included}
              onChange={(e) => update("included", e.target.value)}
            />
          </div>
        )}

        {/* BUYER REQUIREMENTS BUILDER */}
        <div className="rounded-2xl border-2 border-gray-200 p-4">
          <Label className="block mb-1 text-base">Buyer requirements</Label>
          <p className="text-xs text-gray-500 mb-3">
            Define exactly what the buyer must submit after purchase. Required fields gate the order
            entering active work; optional ones can be skipped.
          </p>
          <RequirementsBuilder
            fields={form.requirement_fields}
            onChange={(v) => update("requirement_fields", v)}
          />
        </div>

        {/* SERVICE FAQs */}
        <div className="rounded-2xl border-2 border-gray-200 p-4">
          <Label className="block mb-1 text-base">Service FAQs</Label>
          <p className="text-xs text-gray-500 mb-3">
            Add common buyer questions and your answers. They appear as an
            expandable section on the public service page. Blank Q/A pairs are
            discarded automatically on save.
          </p>
          <FaqBuilder
            items={form.faqs}
            onChange={(v) => update("faqs", v)}
          />
        </div>

        <Button
          type="submit"
          className="bg-mv-primary text-white font-bold w-full sm:w-auto"
          data-testid={mode === "edit" ? "es-submit" : "ns-submit"}
        >
          {mode === "edit" ? "Save changes" : "Publish service"}
        </Button>
      </form>

      {/* Switch-mode confirmation */}
      <Dialog open={!!pendingMode} onOpenChange={(open) => !open && setPendingMode(null)}>
        <DialogContent data-testid="mode-switch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warning className="text-amber-500" weight="duotone" size={20} />
              Switch pricing structure?
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              You have data entered in the
              <b> {form.package_mode === "single" ? "single package" : "three-package tiers"} </b>
              form. Switching to
              <b> {pendingMode === "single" ? "single package" : "three-package tiers"} </b>
              will hide that data — it is not deleted yet, but it will not be saved
              when you publish. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button" variant="outline"
              onClick={() => setPendingMode(null)}
              data-testid="mode-switch-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmModeChange}
              className="bg-mv-primary text-white"
              data-testid="mode-switch-confirm"
            >
              Switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
